import { useEffect, useState } from 'react';
import { getLevelForXP, tierFor } from '@brainfart/shared';
import { cachedRatings } from './ratingsCache';
import { currentStreak, getProfile, saveAchievement } from './profileStore';
import { track } from './telemetry';

// Badges you collect on your profile. Checked where they happen (duel result,
// Daily, level-up, hosting a room…), stored in the profile, and announced
// with a small banner (<AchievementToastHost />) rather than a full-screen
// moment, so they never get in the way of the next game.

export interface Achievement {
  id: string;
  icon: string;
  title: string;
  desc: string;
  /** The small line above the title; the default is the achievement one. */
  kicker?: string;
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: 'first_duel', icon: '⚔️', title: 'First Blood', desc: 'Play your first duel' },
  { id: 'first_win', icon: '🏆', title: 'Winner Winner', desc: 'Win a duel' },
  { id: 'streak_3', icon: '🔥', title: 'Hat-trick', desc: 'Win 3 duels in a row in one topic' },
  { id: 'streak_10', icon: '🌋', title: 'Unstoppable', desc: 'Win 10 duels in a row in one topic' },
  { id: 'perfect_duel', icon: '💯', title: 'Flawless', desc: 'Get all 7 right in a duel' },
  { id: 'giant_slayer', icon: '🗡️', title: 'Giant Slayer', desc: 'Beat someone rated 150+ above you' },
  { id: 'speed_demon', icon: '⚡', title: 'Speed Demon', desc: 'Answer right in under 1.5 seconds' },
  { id: 'gold_tier', icon: '🥇', title: 'Going for Gold', desc: 'Reach Gold in any topic' },
  { id: 'diamond_tier', icon: '💎', title: 'Diamond Mind', desc: 'Reach Diamond in any topic' },
  { id: 'explorer', icon: '🧭', title: 'Explorer', desc: 'Duel in 5 different topics' },
  { id: 'level_5', icon: '⭐', title: 'Pro', desc: 'Reach level 5 in any topic' },
  { id: 'daily_7', icon: '📅', title: 'Week Warrior', desc: 'Keep a 7-day Daily streak' },
  { id: 'daily_30', icon: '🗓️', title: 'Habit Formed', desc: 'Keep a 30-day Daily streak' },
  { id: 'party_host', icon: '🎉', title: 'Party Starter', desc: 'Host a game with friends' },
  { id: 'trash_talk', icon: '💬', title: 'Trash Talker', desc: 'Send a quick-chat message' },
];
export type AchievementId = string;

let queue: Achievement[] = [];
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

export function unlock(id: AchievementId, { quiet = false } = {}) {
  const achievement = ACHIEVEMENTS.find(a => a.id === id);
  if (!achievement || getProfile().achievements[id]) return;
  saveAchievement(id);
  track({ type: 'achievement_unlocked', payload: { id } });
  if (!quiet) {
    queue = [...queue, achievement];
    notify();
  }
}

// A banner that is not a stored achievement (a finished daily quest).
export function announce(item: Achievement) {
  queue = [...queue, item];
  notify();
}

export function dismissAchievementToast() {
  queue = queue.slice(1);
  notify();
}

export function useAchievementToast(): Achievement | null {
  const [, rerender] = useState(0);
  useEffect(() => {
    const l = () => rerender(n => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return queue[0] ?? null;
}

// Everything that can be read straight off the profile. Run after games, and
// once quietly on first launch so existing players get what they've earned.
export function checkProfileAchievements({ quiet = false } = {}) {
  const profile = getProfile();
  const duels = Object.values(profile.duels);
  const played = duels.reduce((n, d) => n + d.wins + d.losses + d.ties, 0);
  if (played >= 1) unlock('first_duel', { quiet });
  if (duels.some(d => d.wins > 0)) unlock('first_win', { quiet });
  if (duels.some(d => d.bestStreak >= 3)) unlock('streak_3', { quiet });
  if (duels.some(d => d.bestStreak >= 10)) unlock('streak_10', { quiet });
  if (Object.keys(profile.duels).length >= 5) unlock('explorer', { quiet });
  if (Object.entries(profile.categoryXP).some(([cat, xp]) => getLevelForXP(xp, cat) >= 5)) unlock('level_5', { quiet });
  const streak = currentStreak();
  if (streak >= 7) unlock('daily_7', { quiet });
  if (streak >= 30) unlock('daily_30', { quiet });
  const best = Math.max(0, ...Object.values(cachedRatings()).map(r => r.peak ?? r.rating));
  if (tierFor(best).min >= tierFor(1150).min) unlock('gold_tier', { quiet });
  if (tierFor(best).min >= tierFor(1550).min) unlock('diamond_tier', { quiet });
}

const INIT_KEY = 'bf_achievements_init';
export function initAchievements() {
  try {
    if (localStorage.getItem(INIT_KEY)) return;
    checkProfileAchievements({ quiet: true });
    localStorage.setItem(INIT_KEY, '1');
  } catch { /* ignore */ }
}
